/**
 * PageShell
 *
 * The wrapper every list/detail page sits in. It owns three things so pages stop re-inventing
 * them:
 *
 *  1. the AppBar title — it calls `usePageTitle(title)` for you, so a page that uses PageShell
 *     can never show the wrong title in the header,
 *  2. responsive gutters — 12px on a phone, 24px at desktop, applied once here,
 *  3. the header row — title/subtitle on the left, `actions` on the right at sm+, and a
 *     full-width action row underneath the title on xs (where a right-aligned button gets
 *     squeezed to nothing).
 *
 * Page width is NOT capped here: the layout's main region already constrains it, and a table
 * page wants every pixel it can get.
 *
 *   <PageShell title="Users" subtitle="1,204 accounts" actions={<Button>Invite</Button>}>
 *     <DataTable ... />
 *   </PageShell>
 */

import { ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { usePageTitle } from '@components/layout/MainLayout'

export interface PageShellProps {
  /** Shown as the page heading AND pushed to the AppBar via usePageTitle. */
  title: string
  /** Optional line under the title — a count, a description, a last-updated stamp. */
  subtitle?: ReactNode
  /** Right-aligned controls at sm+; a full-width row under the title on xs. */
  actions?: ReactNode
  /** Optional <Breadcrumbs> (or anything else) rendered above the title. */
  breadcrumbs?: ReactNode
  children: ReactNode
}

export function PageShell({ title, subtitle, actions, breadcrumbs, children }: PageShellProps) {
  usePageTitle(title)

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        // The shell is the horizontal boundary of the page: anything wider than this
        // (a wide table) has to scroll inside its own container, never move the page.
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      {breadcrumbs && <Box sx={{ mb: 1.5 }}>{breadcrumbs}</Box>}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {actions && (
          <Stack
            direction="row"
            spacing={1}
            // useFlexGap: with plain `spacing`, a button that wraps to a second line keeps the
            // left margin meant for its old neighbour and sits visibly indented.
            useFlexGap
            sx={{
              flexShrink: 0,
              flexWrap: 'wrap',
              rowGap: 1,
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
              // On a phone the actions become a full-width row: every direct child stretches
              // to share the width instead of hugging the right edge.
              '& > *': { flex: { xs: '1 1 auto', sm: '0 0 auto' } },
            }}
          >
            {actions}
          </Stack>
        )}
      </Box>

      {children}
    </Box>
  )
}
